import React, {useRef, useCallback, useEffect, useMemo} from "react";

import DataGrid, {
  Column,
  LoadPanel,
  Scrolling,
  FilterRow,
  GroupPanel,Grouping,
  Selection, Export
} from "devextreme-react/data-grid";
import CachedIcon from "@material-ui/icons/Cached";
import {makeStyles, createStyles} from "@material-ui/core/styles";

import SquareButton from "../SquareButton";
import SearchField from "../SearchField";
import Box from "../Box";
import customizeColumns from "./customizeColumns";
import customizeExcelCell from "./customizeExcelCell";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import TuneIcon from '@material-ui/icons/Tune';
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";

const useStyles = makeStyles((theme) => {
  return createStyles({
    root: {
      display: "flex",
      flexDirection: "row",
      "& #gridContainer": {
        height: "calc(100vh - 200px)"
      }
    },
    controls: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "16px",
      "& > div": {
        display: "flex",
        alignItems: "flex-end",
        "& > *:not(:nth-last-child(1))": {
          marginRight: "14px"
        }
      }
    },
    secondaryMenu: {
      "& ul": {
        padding: 0
      },
      "& > .MuiPaper-root": {
        top: "170px !important"
      },
      padding: 0
    },
    menuItem: {
      fontSize: "12px",
      padding: "17px"
    },
    errorMessage: {
      height: "calc(100vh - 200px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      color: theme.palette.error.dark,
      ...theme.typography.h5
    }
  });
});


function combineRefs(ref1, ref2) {
  return target => {
    if (typeof ref1 === 'function') {
      ref1(target);
    } else if (ref1) {
      ref1.current = target;
    }
    if (typeof ref2 === 'function') {
      ref2(target);
    } else if (ref2) {
      ref2.current = target;
    }
  };
}

const dataGridElementAttrs = {
  id: "gridContainer"
};

const QueryTable = React.forwardRef((props, dataGridForwardRef) => {
  const {
    name,
    data,
    columns,
    status,
    refresh,
    load,
    showFilterAction,
    filters,
    primaryActions,
    secondaryActions,
    focusedId,
    onSelectionChanged,
    onRowClick,
    onFocusedRowChanging,
    onFocusedRowChanged,
    handleShowFilterPanel
  } = props;

  const {controls, secondaryMenu, menuItem, errorMessage} = useStyles();
  const dataGridRef = useRef(null);

  const [secondaryMenuAnchorEl, setSecondaryMenuAnchorEl] = React.useState(null);
  const secondaryMenuOpen = Boolean(secondaryMenuAnchorEl);

  const defaultSecondaryActions = useMemo(() => {
    return [
      {
        label: "Экспортировать выбранные",
        onClick: () => dataGridRef.current.instance.exportToExcel(true)
      },
      {
        label: "Экспортировать все",
        onClick: () => dataGridRef.current.instance.exportToExcel(false)
      },
      {
        label: "Выбор видимых столбцов",
        onClick: () => dataGridRef.current.instance.showColumnChooser()
      },
    ];
  }, [secondaryActions, dataGridRef]);

  useEffect(() => {
    if (dataGridRef.current && dataGridRef.current.instance) {
      if (status.loading) {
        dataGridRef.current.instance.beginCustomLoading();
      } else {
        dataGridRef.current.instance.endCustomLoading();
      }
    }
  }, [status]);

  useEffect(() => {
    if (dataGridRef.current && dataGridRef.current.instance) {
      const dataGrid = dataGridRef.current.instance;
      if (filters && filters.length > 0) {
        dataGrid.filter(filters)
      } else {
        dataGrid.clearFilter();
      }
    }
  }, [filters])

  const searchByText = useCallback((e) => {
    const text = e.target.value;
    dataGridRef.current.instance.searchByText(text);
  }, [dataGridRef]);

  const handleSecondaryMenuClick = useCallback(event => {
    setSecondaryMenuAnchorEl(event.currentTarget);
  }, [setSecondaryMenuAnchorEl]);

  const handleSecondaryMenuClose = useCallback((action) => {
    setSecondaryMenuAnchorEl(null);
    typeof action === 'function' && action();
  }, [setSecondaryMenuAnchorEl]);

  const handleRefresh = useCallback(() => {
    if (status.error) {
      load();
    } else {
      refresh();
    }
  }, [load, refresh, status]);

  const finalRef = useMemo(() => {
    return combineRefs(dataGridRef, dataGridForwardRef)
  }, [dataGridRef, dataGridForwardRef]);

  const clientColumns = useMemo(() => {
    return customizeColumns(columns);
  }, [columns]);

  return (

    <Box>
      <div className={controls}>
        <div>
          <SquareButton onClick={handleRefresh}>
            <CachedIcon fontSize="small"/>
          </SquareButton>
          {showFilterAction &&
          <SquareButton onClick={handleShowFilterPanel}>
            <TuneIcon fontSize="small"/>
          </SquareButton>}
          <SearchField width={400} onChange={searchByText}/>
        </div>
        <div>
          {primaryActions}
          <SquareButton onClick={handleSecondaryMenuClick}>
            <MoreVertIcon fontSize="small"/>
          </SquareButton>
          <Menu
            className={secondaryMenu}
            anchorEl={secondaryMenuAnchorEl}
            keepMounted
            open={secondaryMenuOpen}
            onClose={handleSecondaryMenuClose}
          >
            {secondaryActions && secondaryActions.map(({label, onClick, disabled}) => (
              <MenuItem
                key={label}
                onClick={() => handleSecondaryMenuClose(onClick)}
                className={menuItem}
                disabled={disabled}
              >
                {label}
              </MenuItem>
            ))}
            {secondaryActions && <Divider/>}
            {defaultSecondaryActions.map(({label, onClick, disabled}) => (
              <MenuItem
                key={label}
                onClick={() => handleSecondaryMenuClose(onClick)}
                className={menuItem}
                disabled={disabled}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
      <div>
        {!status.error && <DataGrid
          elementAttr={dataGridElementAttrs}
          ref={finalRef}
          wordWrapEnabled={true}
          dataSource={data}
          onSelectionChanged={onSelectionChanged}
          focusedRowEnabled
          errorRowEnabled={false}
          onRowClick={onRowClick}
          keyExpr={"_key"}
          focusedRowKey={focusedId}
          onFocusedRowChanging={onFocusedRowChanging}
          onFocusedRowChanged={onFocusedRowChanged}
          showBorders>
          <Selection
            mode="multiple"
            showCheckBoxesMode="onClick"
            showBorders={true}
            width={120}
          />
          {clientColumns && clientColumns.map(column => (
            <Column key={column.dataField} {...column} />
          ))}
          <Export fileName={name} customizeExcelCell={customizeExcelCell}/>
          <FilterRow visible={true}/>
          <Scrolling mode="virtual"/>
          <LoadPanel enabled/>
          <GroupPanel visible={false} />
          <Grouping autoExpandAll={true} />
        </DataGrid>}
        {status.error && <div className={errorMessage}>{status.error}</div>}
      </div>
    </Box>
  );
});


export default QueryTable;
